// The two numbers a resource limit is (D83): CPUs and whole GiB of memory, each bounded. The
// admin dialog uses steppers; the group page's Resources card uses sliders.
import { Minus, Plus } from "@phosphor-icons/react";
import { useId } from "react";
import { ActionIcon } from "rizzui";

import { formatNumber, useT } from "../../i18n";

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(n)));

/** A whole number with − and + beside it, kept within min..max. */
export function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  fewer,
  more,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  fewer: string;
  more: string;
}) {
  const id = useId();
  const range = useT().limits.dialog.range(min, max);
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-900">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <ActionIcon variant="outline" aria-label={fewer} disabled={value <= min} onClick={() => onChange(clamp(value - 1, min, max))}>
          <Minus weight="bold" className="h-4 w-4" />
        </ActionIcon>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(clamp(Number(e.target.value) || min, min, max))}
          className="tabular w-24 rounded-md border border-gray-300 bg-transparent px-3 py-2 text-center text-sm text-gray-900 focus:border-primary focus:outline-none"
        />
        <ActionIcon variant="outline" aria-label={more} disabled={value >= max} onClick={() => onChange(clamp(value + 1, min, max))}>
          <Plus weight="bold" className="h-4 w-4" />
        </ActionIcon>
        <span className="text-xs text-gray-500">{range}</span>
      </div>
    </div>
  );
}

/** A slider with its value beside the label, kept within min..max. */
export function RangeField({
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (n: number) => void;
}) {
  const id = useId();
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] font-medium text-gray-700">
          {label}
        </label>
        <span className="tabular text-sm font-semibold text-gray-900">
          {formatNumber(value)}
          {unit ? <span className="ms-1 text-xs font-normal text-gray-500">{unit}</span> : null}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={Math.max(min, max)}
        step={1}
        value={value}
        disabled={max <= min}
        onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
        className="h-2 w-full cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-60"
      />
      <div className="mt-1 flex justify-between text-xs text-gray-500">
        <span>{formatNumber(min)}</span>
        <span>{formatNumber(max)}</span>
      </div>
    </div>
  );
}
